import React from 'react';
import { Tv, Wrench, Wifi, Monitor, Settings, CheckCircle, ArrowRight } from 'lucide-react';

const Services = () => {
  const mainServices = [
    {
      id: 'led-tv-repairs',
      icon: <Tv className="w-12 h-12" />,
      title: 'LED TV Repairs',
      description: 'Complete LED television repair services',
      details: [
        'Backlight issues and dead pixels',
        'Panel problems and screen damage',
        'Power supply failures',
        'Motherboard repairs (board-level)',
        'Audio and sound issues',
        'HDMI port repairs'
      ],
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/jbq6lskk_1000036486.jpg'
    },
    {
      id: 'lcd-tv-repairs',
      icon: <Monitor className="w-12 h-12" />,
      title: 'LCD TV Repairs',
      description: 'Expert LCD television troubleshooting',
      details: [
        'Screen flicker and lines',
        'Color distortion problems',
        'Display burn-in issues',
        'Power button malfunctions',
        'Remote control problems',
        'Inverter board repairs'
      ],
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/l7qtmycz_1000035874.png'
    },
    {
      id: 'smart-tv-support',
      icon: <Wifi className="w-12 h-12" />,
      title: 'Smart TV Support',
      description: 'Software and connectivity solutions',
      details: [
        'Android TV / Google TV issues',
        'Wi-Fi connectivity problems',
        'App installation and updates',
        'Operating system glitches',
        'Streaming service setup',
        'Software upgrades'
      ],
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/03lscane_1000035845.png'
    },
    {
      id: 'tv-installation',
      icon: <Settings className="w-12 h-12" />,
      title: 'TV Installation',
      description: 'Professional mounting and setup',
      details: [
        'Wall mounting services',
        'TV uninstallation',
        'Cable management',
        'Optimal viewing angle setup',
        'Soundbar installation',
        'Complete home theater setup'
      ],
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/vfd2k92s_1000035871.png'
    }
  ];

  const premiumServices = [
    {
      id: 'oled-repairs',
      title: 'OLED TV Repairs',
      description: 'Specialized care for premium OLED displays with delicate panel handling'
    },
    {
      id: 'qled-repairs',
      title: 'QLED TV Repairs',
      description: 'Samsung QLED technology expertise for quantum dot display issues'
    },
    {
      id: '4k-8k-support',
      title: '4K & 8K TV Support',
      description: 'Ultra-HD television repairs with HDR processing expertise'
    },
    {
      id: 'curved-tv-repairs',
      title: 'Curved TV Repairs',
      description: 'Specialized curved screen repairs and panel replacements'
    }
  ];

  const processSteps = [
    {
      id: 'step-1',
      step: '1',
      title: 'Call Us',
      description: 'Contact us at 9565711092 or 6387857396'
    },
    {
      id: 'step-2',
      step: '2',
      title: 'Free Diagnosis',
      description: 'Our technician visits your home for inspection'
    },
    {
      id: 'step-3',
      step: '3',
      title: 'Get Quote',
      description: 'Receive transparent pricing via WhatsApp/call'
    },
    {
      id: 'step-4',
      step: '4',
      title: 'Approve & Repair',
      description: 'We fix your TV once you approve the quote'
    },
    {
      id: 'step-5',
      step: '5',
      title: 'Quality Check',
      description: 'Complete testing before handover'
    },
    {
      id: 'step-6',
      step: '6',
      title: '120-Day Warranty',
      description: 'All repairs covered by our warranty'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Expert Services
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Complete television repair solutions for all modern LED, LCD, Smart TV, and OLED televisions
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mainServices.map((service) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  service.id === 'lcd-tv-repairs' || service.id === 'tv-installation' ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={service.id === 'lcd-tv-repairs' || service.id === 'tv-installation' ? 'lg:order-2' : ''}>
                  <div className="text-gray-900 mb-4">{service.icon}</div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-3">
                    {service.details.map((detail, idx) => (
                      <li key={`${service.id}-${idx}`} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={service.id === 'lcd-tv-repairs' || service.id === 'tv-installation' ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={`${service.title} - Professional TV Repair Service Kanpur`}
                    loading="lazy"
                    className="rounded-xl shadow-2xl w-full object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Services */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Premium TV Repairs
            </h2>
            <p className="text-lg text-gray-600">
              Specialized expertise for high-end television technology
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {premiumServices.map((service) => (
              <div
                key={service.id}
                className="bg-white p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Repair */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              All Major Brands Supported
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Samsung • LG • Sony • OnePlus • Xiaomi • TCL • Haier • Panasonic • Philips • Onida • Lloyd & More
            </p>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-lg p-8">
            <img
              src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/wh6hhqav_1000035872.png"
              alt="We Repair All Major TV Brands - Samsung LG Sony OnePlus Xiaomi"
              loading="lazy"
              className="w-full"
            />
            <p className="text-center text-sm text-gray-600 mt-4">
              *Logos and trademarks shown for representational purposes only
            </p>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Simple Repair Process
            </h2>
            <p className="text-lg text-gray-600">
              From your call to warranty - a transparent, hassle-free journey
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((item) => (
              <div key={item.id} className="relative bg-white p-6 rounded-xl border border-gray-200 shadow-md">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 mt-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Banner */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900 text-white rounded-2xl overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-8 lg:p-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Affordable Pricing Starting at ₹299
                </h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-1" />
                    <span>Free diagnosis if repaired</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-1" />
                    <span>No hidden charges - transparent quotes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-1" />
                    <span>No repair, no charge (only ₹299 visit fee)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-1" />
                    <span>120-day warranty on all repairs</span>
                  </li>
                </ul>
                <a
                  href="tel:9565711092"
                  className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
                >
                  Book Service Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </div>
              <div className="relative h-80 lg:h-96 rounded-xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/e9vu89f6_1000036533.png"
                  alt="Affordable TV Repair Pricing - Starting ₹299"
                  loading="lazy"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              We Serve All Across Kanpur
            </h2>
            <p className="text-lg text-gray-600">
              Same-day doorstep service in every area of Kanpur
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-center">
            {[
              'Nehru Nagar',
              'Govind Nagar',
              'Kalyanpur',
              'Swaroop Nagar',
              'Kakadeo',
              'Shyam Nagar',
              'Lal Bangla',
              'Jajmau',
              'Saket Nagar',
              'Gumti No. 5',
              'Pandu Nagar',
              'Shastri Nagar',
              'Yashoda Nagar',
              'Cantt Area',
              'Nawabganj',
              'Tilak Nagar',
              'Bithoor',
              'Rawatpur',
              'Juhi',
              'Harsh Nagar'
            ].map((area) => (
              <div
                key={area}
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="text-gray-700 font-medium">{area}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-lg font-semibold text-teal-600 mt-8">
            & All Other Areas Across Kanpur
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Fix Your TV?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Call now for same-day doorstep service. Expert technicians. 120-day warranty.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9565711092"
              className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white text-lg font-semibold rounded-xl hover:bg-gray-800 transition-colors shadow-lg"
            >
              Call: 9565711092
            </a>
            <a
              href="https://wa.me/918299034722"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white text-lg font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-lg"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
